import { Component, Input, OnChanges, HostListener } from '@angular/core';
import { Platform } from '@ionic/angular';

interface FingerPosition {
  x: number;
  y: number;
  rotation: number;
}

@Component({
  selector: 'app-hand-overlay',
  template: `
    <div class="hands-container" [style.transform]="getContainerTransform()">
      <!-- Left Hand (Uyir) -->
      <div class="hand left-hand" [style]="getLeftHandStyle()">
        <!-- Only show resting hand when right hand is active -->
        <img *ngIf="!getActiveFingerPosition()?.startsWith('left-')"
             src="assets/hand/left-resting-hand.png" 
             class="hand-base" 
             alt="left hand" />
        <!-- Show active finger overlay when left hand is active -->
        <img *ngIf="getActiveFingerPosition()?.startsWith('left-')"
             [src]="getFingerImage(getActiveFingerPosition())"
             class="finger-overlay"
             alt="active finger" />
      </div>

      <!-- Right Hand (Mei) -->
      <div class="hand right-hand" [style]="getRightHandStyle()">
        <!-- Only show resting hand when left hand is active -->
        <img *ngIf="!getActiveFingerPosition()?.startsWith('right-')"
             src="assets/hand/right-resting-hand.png" 
             class="hand-base" 
             alt="right hand" />
        <!-- Show active finger overlay when right hand is active -->
        <img *ngIf="getActiveFingerPosition()?.startsWith('right-')"
             [src]="getFingerImage(getActiveFingerPosition())"
             class="finger-overlay"
             alt="active finger" />
      </div>
    </div>
  `,
  styles: [`
    .hands-container {
      position: absolute;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 1100;
      top: 0;
      left: 0;
      transition: transform 0.3s ease;
    }

    .hand {
      position: absolute;
      transition: all 0.3s ease;
    }

    .hand-base, .finger-overlay {
      width: 100%;
      height: 100%;
      object-fit: contain;
      transition: all 0.2s ease-out;
    }

    .hand-base {
      opacity: 0.6;
    }

    .finger-overlay {
      opacity: 0.8;
    }

    @media (max-width: 1024px) {  /* Changed from 768px to 1024px to include tablets/iPads */
      .hands-container {
        display: none;
      }
    }
  `]
})
export class HandOverlayComponent implements OnChanges {
  @Input() currentKey: string = '';
  @Input() keyboardLayout: any;

  private windowWidth: number = window.innerWidth;
  private windowHeight: number = window.innerHeight;

  constructor(private platform: Platform) {}

  // Helper method to detect Chrome on Mac
  private isChromeOnMac(): boolean {
    return /Chrome/.test(navigator.userAgent) && /Mac/.test(navigator.platform);
  }

  // Add this helper method to detect Windows platforms
  private isWindows(): boolean {
    return /Win/.test(navigator.platform);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.windowWidth = event.target.innerWidth;
    this.windowHeight = event.target.innerHeight;
  }

  getContainerTransform(): string {
    let scale = 1;
    if (this.windowWidth > 1440) {
      scale = 1.1;
    }
    return `scale(${scale})`;
  }

  getLeftHandStyle(): string {
    const baseWidth = Math.min(900, this.windowWidth * 0.7);
    const baseHeight = baseWidth * 0.75;
    
    const bottomPosition = this.windowWidth > 1440 
      ? '6%'   // iMac
      : '-14%'; // MacBook
    
    const horizontalOffset = this.windowWidth > 1440
      ? '0px'  // iMac
      : '20px'; // MacBook
    
    // Base transform plus extra translateY if Chrome on Mac or on Windows
    const baseTransform = `rotate(0deg) rotateX(0deg)`;
    const extraTransform = (this.isChromeOnMac() || this.isWindows()) ? ' translateY(40px)' : '';
    
    return `
      width: ${baseWidth}px;
      height: ${baseHeight}px;
      bottom: ${bottomPosition};
      left: calc(50% - ${baseWidth * 0.75}px - ${horizontalOffset});
      transform: ${baseTransform + extraTransform};
    `;
  }

  getRightHandStyle(): string {
    const baseWidth = Math.min(900, this.windowWidth * 0.7);
    const baseHeight = baseWidth * 0.75;
    
    const bottomPosition = this.windowWidth > 1440 
      ? '6%'   // iMac
      : '-14%'; // MacBook
    
    const horizontalOffset = this.windowWidth > 1440
      ? '0px'  // iMac
      : '20px'; // MacBook
    
    // Base transform plus extra translateY if Chrome on Mac or on Windows
    const baseTransform = `rotate(2deg) rotateX(0deg)`;
    const extraTransform = (this.isChromeOnMac() || this.isWindows()) ? ' translateY(40px)' : '';
    
    return `
      width: ${baseWidth}px;
      height: ${baseHeight}px;
      bottom: ${bottomPosition};
      right: calc(50% - ${baseWidth * 0.75}px - ${horizontalOffset});
      transform: ${baseTransform + extraTransform};
    `;
  }

  // Update finger positions array to be more consistent
  fingerPositions = [
    'left-top-pinky', 'left-home-pinky', 'left-bottom-pinky',
    'left-top-ring', 'left-home-ring', 'left-bottom-ring', 
    'left-top-middle', 'left-home-middle', 'left-bottom-middle',
    'left-top-index', 'left-home-index', 'left-bottom-index',
    'left-top-index-extra', 'left-home-index-extra', 'left-bottom-index-extra',
    
    'right-top-index-extra', 'right-home-index-extra', 'right-bottom-index-extra',
    'right-top-index', 'right-home-index', 'right-bottom-index',
    'right-top-middle', 'right-home-middle', 'right-bottom-middle',
    'right-top-ring', 'right-home-ring', 'right-bottom-ring',
    'right-top-pinky', 'right-home-pinky', 'right-bottom-pinky'
  ];

  private positionTransforms: { [key: string]: FingerPosition } = {
    // Left hand positions - adjusted down slightly
    'left-top-pinky': { x: -90, y: -120, rotation: -20 },    // Q (அ)
    'left-home-pinky': { x: -90, y: -80, rotation: -15 },    // A (உ) - Home position
    'left-bottom-pinky': { x: -90, y: -40, rotation: -10 },  // Z (ஐ)
    
    'left-top-ring': { x: -60, y: -120, rotation: -15 },     // W (ஆ)
    'left-home-ring': { x: -60, y: -80, rotation: -10 },     // S (ஊ)
    'left-bottom-ring': { x: -60, y: -40, rotation: -5 },    // X (ஒ)
    
    'left-top-middle': { x: -30, y: -120, rotation: -10 },   // E (இ)
    'left-home-middle': { x: -30, y: -80, rotation: -5 },    // D (எ)
    'left-bottom-middle': { x: -30, y: -40, rotation: 0 },   // C (ஓ)
    
    'left-top-index': { x: 0, y: -120, rotation: -5 },       // R (ஈ)
    'left-home-index': { x: 0, y: -80, rotation: 0 },        // F (ஏ)
    'left-bottom-index': { x: 0, y: -40, rotation: 5 },      // V (ஔ)
    
    // Right hand positions - adjusted down slightly
    'right-top-index': { x: 30, y: -120, rotation: 5 },      // Y (ச்)
    'right-home-index': { x: 30, y: -80, rotation: 0 },      // H (ஞ்)
    'right-bottom-index': { x: 30, y: -40, rotation: -5 },   // N (ர்)
    
    'right-top-middle': { x: 60, y: -120, rotation: 10 },    // U (ட்)
    'right-home-middle': { x: 60, y: -80, rotation: 5 },     // J (ண்)
    'right-bottom-middle': { x: 60, y: -40, rotation: 0 },   // M (ல்)
    
    'right-top-ring': { x: 90, y: -120, rotation: 15 },      // I (த்)
    'right-home-ring': { x: 90, y: -80, rotation: 10 },      // K (ந்)
    'right-bottom-ring': { x: 90, y: -40, rotation: 5 },     // , (வ்)
    
    'right-top-pinky': { x: 120, y: -120, rotation: 20 },    // O/P (ப்/ற்)
    'right-home-pinky': { x: 120, y: -80, rotation: 15 },    // L/; (ம்/ன்)
    'right-bottom-pinky': { x: 120, y: -40, rotation: 10 }   // ./? (ழ்/ள்)
  };

  // Update Tamil keyboard mapping to use correct finger positions
  private tamilFingerMap: { [key: string]: string } = {
    // Left hand (Uyir) - Top row
    'அ': 'left-top-pinky',      // Q
    'ஆ': 'left-top-ring',       // W
    'இ': 'left-top-middle',     // E
    'ஈ': 'left-top-index',      // R

    // Left hand - Home row
    'உ': 'left-home-pinky',     // A
    'ஊ': 'left-home-ring',      // S
    'எ': 'left-home-middle',    // D
    'ஏ': 'left-home-index',     // F

    // Left hand - Bottom row
    'ஐ': 'left-bottom-pinky',   // Z
    'ஒ': 'left-bottom-ring',    // X
    'ஓ': 'left-bottom-middle',  // C
    'ஔ': 'left-bottom-index',   // V

    // Keys T, G, B - using left index finger extra positions
    'க்': 'left-top-index-extra',     // T
    'ங்': 'left-home-index-extra',    // G
    'ய்': 'left-bottom-index-extra',  // B

    // Right hand (Mei) - corrected finger assignments for all rows
    // Top row
    'ச்': 'right-top-index-extra',    // Y (index extra reaching)
    'ட்': 'right-top-index',          // U (index)
    'த்': 'right-top-middle',         // I (middle)
    'ப்': 'right-top-ring',           // O (ring)
    'ற்': 'right-top-pinky',          // P (pinky)

    // Home row
    'ஞ்': 'right-home-index-extra',   // H (index extra)
    'ண்': 'right-home-index',         // J (index)
    'ந்': 'right-home-middle',        // K (middle)
    'ம்': 'right-home-ring',          // L (ring)
    'ன்': 'right-home-pinky',         // ; (pinky)

    // Bottom row
    'ர்': 'right-bottom-index-extra', // N (index extra)
    'ல்': 'right-bottom-index',       // M (index)
    'வ்': 'right-bottom-middle',      // , (middle)
    'ழ்': 'right-bottom-ring',        // . (ring)
    'ள்': 'right-bottom-pinky'        // / (pinky)
  };

  getFingerImage(position: string): string {
    return position ? `assets/hand/${position}.png` : '';
  }

  getFingerTransform(position: string): string {
    const pos = this.positionTransforms[position];
    if (!pos) return 'none';

    const pressY = pos.y + 10; // Always show finger in pressed position
    return `translate(${pos.x}px, ${pressY}px) rotate(${pos.rotation}deg)`;
  }

  getActiveFingerPosition(): string {
    return this.tamilFingerMap[this.currentKey] || '';
  }

  ngOnChanges() {
    // No additional handling needed
  }
} 